import React, { useEffect, useState } from 'react';
import { associateApi } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

/* The Associate profile popup.

   Deliberately built on the SAME primitives and the same layout tokens as the
   Staff edit dialog in EmployeeProfile.js — `max-w-2xl max-h-[90vh]
   overflow-y-auto` on the content, `grid grid-cols-2 gap-4` for the fields,
   the same label and value typography. The two screens are the same software
   and should not need a second look to confirm it. The Staff dialog itself is
   untouched.

   Read-only on purpose: editing an associate already has its own inline row
   editor on the register, and a second place to change the same fields is a
   second place for them to disagree. The pencil still opens that editor; this
   popup only shows.

   Everything comes from ONE call keyed on the associate's id, so the popup
   cannot show a half-loaded mixture of two people, and so "Not Recorded" is
   decided server-side in one place rather than by each field guessing. */

const NOT_RECORDED = 'Not Recorded';

/* A missing value is shown as missing. Never blank, never a zero, never an
   assumed "Fresher" — the register has never had anywhere to record most of
   these for shop-floor staff, and a confident-looking wrong answer is worse
   than an honest gap. */
function Field({ label, value, hint }) {
  const empty = value === null || value === undefined || value === '';
  return (
    <div className="min-w-0">
      <p className="text-xs font-semibold text-[#5A6377] mb-1">{label}</p>
      <p className={`text-sm break-words ${empty ? 'text-[#C9CFDB] italic' : 'text-[#11182B]'}`}>
        {empty ? NOT_RECORDED : value}
      </p>
      {!empty && hint && <p className="text-[11px] text-[#98A1B2] mt-0.5">{hint}</p>}
    </div>
  );
}

function Section({ title, children, note }) {
  return (
    <div className="pt-4 mt-4 border-t border-[#F0F2F6] first:border-0 first:mt-0 first:pt-0">
      <h3 className="text-[11px] font-bold uppercase tracking-wide text-[#98A1B2] mb-3">{title}</h3>
      <div className="grid grid-cols-2 gap-4">{children}</div>
      {note && <p className="text-[11px] text-[#98A1B2] mt-3">{note}</p>}
    </div>
  );
}

const rupees = (n) => (n == null || n === '' ? null : `₹${Math.round(n).toLocaleString('en-IN')}`);

function LevelPill({ level, label }) {
  if (!level) return <span className="text-[#C9CFDB] italic text-sm">Not Assessed</span>;
  const champs = Number(level) === 4;
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${
      champs ? 'bg-[#FFF4E5] text-[#8A4B00] border-[#FFD9A8]'
             : 'bg-[#EFF3FB] text-[#2F4A77] border-[#D9E2F3]'}`}>
      {label || `L${level}`}
    </span>
  );
}

export default function AssociateProfileDialog({ associateId, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !associateId) return;
    // Cleared first so the previous associate's details are never on screen
    // under the next one's name while this call is in flight.
    setData(null); setError('');
    let alive = true;
    associateApi.profile(associateId)
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || 'Could not load this profile'); });
    return () => { alive = false; };
  }, [associateId, open]);

  const a = data?.associate;
  const exp = data?.experience;
  const sm = data?.skill_matrix;
  const latest = sm?.latest;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Same width, height cap and scroll behaviour as the Staff dialog. */}
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto"
                     data-testid="associate-profile-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span>{a?.name || 'Associate profile'}</span>
            {a && <LevelPill level={a.level} label={data?.level_label} />}
            {a?.status === 'Left' && (
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#FDE7E9] text-[#E11D2A]">
                Left{a.last_working_day ? ` · ${a.last_working_day}` : ''}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {error && <p className="text-sm text-[#E11D2A] py-6">{error}</p>}
        {!data && !error && (
          <div className="py-10 text-center text-sm text-[#98A1B2]">Loading…</div>
        )}

        {data && (
          <div className="pb-2">
            <Section title="Personal">
              <Field label="Name" value={a.name} />
              <Field label="Employee Code" value={a.code} />
              <Field label="Father / Husband" value={a.father_husband} />
              <Field label="Date of Birth" value={a.dob} />
              <Field label="Contact Number" value={a.mobile} />
              <Field label="Email" value={a.email} />
            </Section>

            <Section title="Employment">
              <Field label="Unit" value={a.unit} />
              <Field label="Department" value={a.department} />
              <Field label="Contractor"
                     value={data.contractor?.short_name || a.contractor_name}
                     hint={a.is_own_payroll ? 'On company payroll' : null} />
              <Field label="Designation" value={a.designation}
                     hint={a.source_designation && a.source_designation !== a.designation
                       ? `Export says: ${a.source_designation}` : null} />
              <Field label="Current Level"
                     value={a.level ? (data.level_label || `L${a.level}`) : null}
                     hint={a.level_source === 'skill_matrix' ? 'Set by Skill Matrix assessment'
                       : a.level_source === 'approved_sheet' ? 'Set by the approved structure' : null} />
              <Field label="Status" value={a.status} />
              <Field label="Date of Joining" value={a.doj} />
              <Field label="Salary" value={rupees(a.salary)} />
            </Section>

            <Section title="Education">
              <Field label="Qualification" value={a.education} />
              <Field label="Course / Stream" value={a.course} />
              <Field label="Institution" value={a.institution} />
              <Field label="Year of Passing" value={a.year_passing} />
            </Section>

            {/* Three separate facts, not one blended number: what they brought,
                what they have served here, and the sum. The total is computed
                on every read from the joining date — it is never stored and
                never incremented by a job, so it cannot drift. */}
            <Section title="Experience"
                     note={exp?.total_years != null
                       ? 'Total = experience at joining + service since the date of joining, calculated now.'
                       : null}>
              <Field label="At Joining" value={exp?.type} />
              <Field label="Previous Experience"
                     value={exp?.type ? exp.prior_label : null} />
              <Field label="Service at AAMPL" value={exp?.service_years != null ? exp.service_label : null}
                     hint={!a.doj ? null : undefined} />
              <Field label="Total Experience Now"
                     value={exp?.total_years != null ? exp.total_label : null} />
            </Section>

            <Section title="Recruitment">
              <Field label="Source of Hiring" value={a.source_of_hiring} />
              <Field label="Referred By / Details" value={a.source_detail} />
            </Section>

            <Section title="Skill Matrix"
                     note={sm?.assessed ? null
                       : 'No assessment recorded yet — the level is not a test result.'}>
              <Field label="Current Level"
                     value={a.level ? (data.level_label || `L${a.level}`) : null} />
              <Field label="Latest Score"
                     value={latest ? `${latest.average_percent}%` : null} />
              <Field label="Last Assessed"
                     value={latest ? (latest.assessed_on || String(latest.assessed_at).slice(0, 10)) : null}
                     hint={latest?.assessor ? `by ${latest.assessor}` : null} />
              <Field label="Assessments on Record"
                     value={sm?.history?.length ? String(sm.history.length) : null} />
            </Section>

            {/* History is a list, so it sits outside the two-column grid. */}
            {sm?.history?.length > 1 && (
              <div className="mt-4">
                <h3 className="text-[11px] font-bold uppercase tracking-wide text-[#98A1B2] mb-2">
                  Assessment history
                </h3>
                <div className="border border-[#EDF0F5] rounded-lg divide-y divide-[#F2F4F8]">
                  {sm.history.map((h, i) => (
                    <div key={h.id || i} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                      <span className="text-[#5A6377]">
                        {h.assessed_on || String(h.assessed_at).slice(0, 10)}
                      </span>
                      <span className="text-[#11182B] tabular-nums">{h.average_percent}%</span>
                      <LevelPill level={h.level} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.designation_history?.length > 0 && (
              <p className="text-[11px] text-[#98A1B2] mt-4">
                Previously filed as{' '}
                {data.designation_history.map((h) => h.from).join(', ')} — moved by the
                approved Associate structure.
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
